/** @module utils/history */
import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import eventEmitter from "web/script/mixins/event-emitter";
import globals from "web/script/modules/globals";
import browser from "./browser";
import diff from "./diff";
import uuid from "./uuid";
import url from "./url";

/**
 * Returns true if `o` is a plain object. Different from Underscores _.isObject which
 * returns true for arrays, functions, regex's etc...
 * @param {*} o
 * @returns {boolean}
 */
function isObject(o) {
    return Object.prototype.toString.call(o) === "[object Object]";
}

/**
 * Creates a new browser history object which wraps the History API.
 * @alias module:utils/history
 * @constructor
 */
function BrowserHistory() {
    this._onLocationChange = this._onLocationChange.bind(this);
    this._onHashChange = this._onHashChange.bind(this);
}

_.extend(
    BrowserHistory.prototype,
    eventEmitter,
    /** @lends module:utils/history.prototype */
    {
        _window: null,
        _currentQuery: null,
        _currentPath: null,

        /**
         * Keeps a history of scroll positions when a new state is pushed. This is restored
         * when the state has been popped (i.e. gone back).
         * @type {Array}
         */
        _scrollPositionHistoryQueue: null,

        /**
         * Starts listening for history changes, and fires any history being listened to.
         * @returns {BrowserHistory}
         */
        start: function(win) {
            if (this._window) {
                throw new Error("History has already been started.");
            }

            this._window = win || globals.window;
            this._scrollPositionHistoryQueue = [];

            this._window.addEventListener("popstate", this._onLocationChange);
            this._window.addEventListener("hashchange", this._onHashChange);

            // replace the initial state with one that contains a uuid
            if (browser.supportsHistory) {
                this._updateHistory(
                    "replaceState",
                    this.getCurrentPath(),
                    this.getCurrentQueryParams(),
                    this._window.location.hash,
                    document.title,
                    {
                        uuid: uuid.uuid4(),
                        pageViewId: environment.get("pageViewId"),
                    }
                );
            }

            return this;
        },

        /**
         * Unbinds all event listeners and resets all state.
         */
        stop: function() {
            if (!this._window) {
                return this;
            }

            this._scrollPositionHistoryQueue = null;
            this._currentPath = null;
            this._currentQuery = null;
            this._window.removeEventListener("popstate", this._onLocationChange);
            this._window.removeEventListener("hashchange", this._onHashChange);
            this._window = null;
            return this;
        },

        /**
         * Replaces a new state into history. The argument order of this method is different
         * from the browser API to reflect the most common use cases.
         * @param {string} path
         * @param {object} [query]
         * @param {string} [title]
         * @param {object} [data]
         * @returns {BrowserHistory}
         */
        replaceState: function(path, query, title, data) {
            return this._updateHistory("replaceState", path, query, null, title, data);
        },

        /**
         * Pushes the current state in the history. The argument order of this method is
         * different from the browser API to reflect the most common use cases.
         * @param {string} path
         * @param {object} [query]
         * @param {string} [title]
         * @param {object} [data]
         * @returns {BrowserHistory}
         */
        pushState: function(path, query, title, data) {
            return this._updateHistory("pushState", path, query, null, title, data);
        },

        /**
         * Causes the history to go back one step. This may cause a new page load or a
         * 'popstate' event depending on how the current state was triggered.
         * @returns {BrowserHistory}
         */
        back: function() {
            this._window.history.back();
            return this;
        },

        /**
         * Returns the current pathname.
         * @returns {string}
         */
        getCurrentPath: function() {
            return this._window.location.pathname;
        },

        /**
         * Returns an object containing the current query parameters.
         * @returns {object}
         */
        getCurrentQueryParams: function() {
            return url.toURLSearchParams(this._window.location.search);
        },

        /**
         * Updates the query string of the ur without changing the path.
         * @param {string|object} keyOrMap String key or an object of new query parameters.
         * @param {*} valueOrReplace Value or replace.
         * @param {boolean} [replace] If using 'key' and 'value' this should be true if current
         *                            history state should be replaced with the new query.
         */
        updateQuery: function(keyOrMap, valueOrReplace, replace) {
            var params = keyOrMap;

            if (isObject(params) || params instanceof url.URLSearchParams) {
                replace = valueOrReplace;
            } else if (_.isString(params)) {
                var tmp = {};
                tmp[params] = valueOrReplace;
                params = tmp;
            } else {
                throw new Error("The first argument to updateQuery should be a string or object.");
            }

            return this._updateHistory(
                replace ? "replaceState" : "pushState",
                this.getCurrentPath(),
                url.toURLSearchParams(params),
                this._window.location.hash
            );
        },

        /**
         * Convenience method for subscribing to a particular query parameter.
         * The main different with this method in comparison to 'on' is that the
         * callback will be called straight away if the value of the parameter is
         * truth-y at the time of call.
         * @param {string} key The key to subscribe to.
         * @param {function} callback The callback to call when it changes.
         * @returns {function} A function that if called unbinds the handler.
         */
        bindQuery: function(key, callback) {
            var value;

            if (this._window) {
                value = this.getCurrentQueryParams().getAll(key);

                // if value is already present and the history module has
                // already been started then call the callback.
                if (value.length) {
                    callback("added", value, undefined);
                }
            }

            function handler(event, data) {
                var record = _.findWhere(data.queryChanges, { key: key });

                if (!record || _.isEqual(record.value, value)) {
                    return;
                }

                value = record.value;
                callback(record.type, record.value, record.oldValue);
            }

            this.on("change", handler);

            // return a function that can remove this binding
            return function() {
                this.off("change", handler);
            }.bind(this);
        },

        /**
         * Updates the history state using the given method.
         * @param method
         * @param path
         * @param [query]
         * @param [hash]
         * @param [title]
         * @param [state]
         * @returns {*}
         * @private
         */
        _updateHistory: function(method, path, query, hash, title, state) {
            if (_.isEmpty(path)) {
                return this;
            }

            var parsed = url.parse(path);

            // extend any query params present on `path` with ones provided in `query`
            var mergedParams = _.extend(
                parsed.searchParams.toJSON(),
                url.toURLSearchParams(query).toJSON()
            );
            parsed.searchParams = url.toURLSearchParams(mergedParams);
            parsed.hash = hash || "";

            var changes = this._getChanges(this._currentQuery, parsed.searchParams);

            if (!changes.length && parsed.path === this._currentPath) {
                return this;
            }

            if (!browser.supportsHistory) {
                this._window.location = url.unparse(parsed);
                return;
            }

            // fix scroll position bugs in certain browsers, only for pushState
            if (method === "pushState") {
                this._scrollPositionHistoryQueue.push(this._window.pageYOffset);
            }

            state = state || {};
            if (!state.uuid) {
                state.uuid = uuid.uuid4();
            }

            if (!state.pageViewId) {
                // Set the pageViewID associated with this page state
                analytics.generatePageViewId();
                state.pageViewId = analytics.getPageViewId();
            }

            this._window.history[method](state, title, url.unparse(parsed));
            this._onLocationChange({
                state: state,
                type: method,
            });
            return this;
        },

        /**
         *
         * @param [event] If event is passed then it is being called from 'popstate' event,
         *                otherwise it is being called manually following a pushState or
         *                replaceState.
         * @private
         */
        _onLocationChange: function(event) {
            if (event.type === "popstate" && event.state === null) {
                console.error("Received a popstate event with no state object.");
                return;
            }

            var oldQuery = this._currentQuery;
            var oldPath = this._currentPath;
            var query = this.getCurrentQueryParams();
            var path = this._window.location.pathname;
            var queryChanges = this._getChanges(oldQuery, query);

            this._currentQuery = query;
            this._currentPath = path;

            this.trigger("change", {
                queryChanges: queryChanges,
                path: path,
                oldPath: oldPath,
                oldQuery: oldQuery,
                query: query,
                state: event.state,
                eventType: event.type,
            });

            // if event, then it's a popstate.
            if (event && event.type === "popstate") {
                // Restore the pageViewId
                const state = this._window.history.state;
                if (state) {
                    analytics.setPageViewId(state.pageViewId);
                }
                // Restore the scroll position for browsers that are
                // buggy in restoring it on changing the URL on back.
                var scrollPosition = this._scrollPositionHistoryQueue.pop();
                if (_.isNumber(scrollPosition)) {
                    _.defer(
                        function() {
                            this._window.scrollTo(0, scrollPosition);
                        }.bind(this)
                    );
                }
            }
        },

        /**
         * Handler for the hashchange event.
         * @param event
         * @private
         */
        _onHashChange: function(event) {
            this.trigger("hashchange", event);
        },

        /**
         * Calculates the changes between two sets of query params (ie. added, changed, removed.)
         * @param {URLSearchParams} queryA
         * @param {URLSearchParams} queryB
         * @returns {[]}
         */
        _getChanges: function(queryA, queryB) {
            queryA = url.toURLSearchParams(queryA).toJSON();
            queryB = url.toURLSearchParams(queryB).toJSON();

            return diff(queryA, queryB);
        },
    }
);

// create the instance exported by this module
var browserHistory = new BrowserHistory();

// expose the class for testing
browserHistory.BrowserHistory = BrowserHistory;

// return the instance
export default browserHistory;
